import { PreviewRecord, storePreviewUrl } from "@/storage/indexeddb"
import { PNApiOperations } from "@/api/operations"
import { ImageFormat, processImageUrl } from "@/utils/process-image-url"
import { Sentry } from "@/telemetry/sentry"
import { retry } from "@/utils/retry"

export class PreviewService {
	/**
	 *
	 * Uploads the image contents of the supplied preview records to s3.
	 *
	 * Side effects:
	 *   - Mutates `previews` members' `cloudSyncStatus` and `cloudUrl` upon successful upload
	 *   - Mutates corresponding PreviewRecords in IndexedDB in the same way
	 *
	 * @param previews Array of preview records containing images to upload
	 * @returns array of PromiseSettledResult for the individual upload operations
	 *
	 */
	static async uploadPreviews(previews: PreviewRecord[]) {
		const uploadUrls = await PNApiOperations.getSignedUrls(
			previews.map(() => ({
				contentType: "image",
				entityType: "project-preview",
				contentDigest: undefined,
			})),
			{ networkIntent: "background" },
		)

		return Promise.allSettled(
			previews.map(async (preview, index) => {
				const uploadUrl = uploadUrls[index]

				if (!uploadUrl) {
					throw new Error("Couldn't match thumbnail with upload url")
				}

				const { url } = await PNApiOperations.uploadContent({
					data: preview.data,
					url: uploadUrl.url,
					networkIntent: "background",
				})

				void storePreviewUrl(preview.id, url).catch(Sentry.captureException)

				// Eagerly trigger AVIF image processing -- deferring this will cause
				// thumbnails to take upwards of 10s to load in project listing
				// due to image processor request times.
				void retry(
					() => fetch(processImageUrl({ url, format: ImageFormat.AVIF })),
					[],
					3,
				).catch((_e) => {
					// rejection ignored as it indicates a connectivity issue
				})

				preview.cloudSyncStatus = "synced"
				preview.cloudUrl = url
			}),
		)
	}

	static async uploadPreview(preview: PreviewRecord) {
		const uploadUrl = (
			await PNApiOperations.getSignedUrls(
				[
					{
						contentType: "image",
						entityType: "project-preview",
						contentDigest: undefined,
					},
				],
				{
					networkIntent: "background",
				},
			)
		)[0]

		if (!uploadUrl) {
			throw new Error("Couldn't match thumbnail with upload url")
		}

		const { url } = await PNApiOperations.uploadContent({
			data: preview.data,
			url: uploadUrl.url,
			networkIntent: "background",
		})

		void storePreviewUrl(preview.id, url).catch(Sentry.captureException)

		// Eagerly trigger AVIF image processing -- deferring this will cause
		// thumbnails to take upwards of 10s to load in project listing
		// due to image processor request times.
		void retry(() => fetch(processImageUrl({ url, format: ImageFormat.AVIF })), [], 3).catch(
			(_e) => {
				// rejection ignored as it indicates a connectivity issue
			},
		)

		preview.cloudSyncStatus = "synced"
		preview.cloudUrl = url
		return preview
	}
}
